// frontend/src/components/dashboard/InsightPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lightbulb, AlertTriangle, Flame, Trophy } from 'lucide-react';
import { leadershipEngine } from '../../services/leadershipEngine/leadershipEngine';

/* =======================
   PALETA — AURORA (HERO)
======================= */
const AURORA = {
  emerald: '#34D399',
  purple: '#A78BFA',
  amber: '#F59E0B',
  border: 'rgba(255,255,255,0.10)',
  text: 'rgba(255,255,255,0.92)',
  text2: 'rgba(255,255,255,0.65)',
  text3: 'rgba(255,255,255,0.45)',
};

const MESES_PT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const formatMoney = (v = 0) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(v) || 0);

function AccentLine({ variant = 'mix' }) {
  const bg =
    variant === 'emerald'
      ? `linear-gradient(90deg, ${AURORA.emerald}, rgba(52,211,153,0.15))`
      : variant === 'purple'
        ? `linear-gradient(90deg, ${AURORA.purple}, rgba(167,139,250,0.15))`
        : variant === 'amber'
          ? `linear-gradient(90deg, ${AURORA.amber}, rgba(245,158,11,0.14))`
          : `linear-gradient(90deg, ${AURORA.emerald}, ${AURORA.purple})`;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
      style={{ background: bg }}
    />
  );
}

function AuroraCard({
  eyebrow,
  title,
  icon: Icon,
  accent = 'emerald',
  children,
  compact = false,
  tone = 'default', // default | warning | danger | success
  rightSlot,
}) {
  const pad = compact ? 'p-4' : 'p-5';

  const halo =
    accent === 'purple'
      ? 'rgba(167,139,250,0.16)'
      : accent === 'amber'
        ? 'rgba(245,158,11,0.14)'
        : accent === 'cyan'
          ? 'rgba(34,211,238,0.14)'
          : 'rgba(52,211,153,0.14)';

  const iconBg =
    accent === 'purple'
      ? 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(167,139,250,0.06))'
      : accent === 'amber'
        ? 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.07))'
        : accent === 'cyan'
          ? 'linear-gradient(135deg, rgba(34,211,238,0.18), rgba(34,211,238,0.06))'
          : 'linear-gradient(135deg, rgba(52,211,153,0.18), rgba(52,211,153,0.06))';

  const toneStyle =
    tone === 'danger'
      ? {
          background: 'rgba(255,255,255,0.05)',
          borderColor: 'rgba(248,113,113,0.25)',
        }
      : tone === 'warning'
        ? {
            background: 'rgba(255,255,255,0.05)',
            borderColor: 'rgba(245,158,11,0.25)',
          }
        : tone === 'success'
          ? {
              background: 'rgba(255,255,255,0.05)',
              borderColor: 'rgba(52,211,153,0.22)',
            }
          : {
              background: 'rgba(255,255,255,0.05)',
              borderColor: AURORA.border,
            };

  const accentVariant =
    accent === 'purple' ? 'purple' : accent === 'amber' ? 'amber' : 'emerald';

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border bg-zinc-950/80 backdrop-blur ${pad}`}
      style={toneStyle}
    >
      <AccentLine variant={accentVariant} />

      {/* halos */}
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full blur-3xl"
        style={{ background: halo }}
      />
      <div
        className="pointer-events-none absolute -left-12 -bottom-12 h-44 w-44 rounded-full blur-3xl"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      />

      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {eyebrow && (
              <div
                className="text-[10px] font-black uppercase tracking-[0.26em]"
                style={{ color: 'rgba(167,243,208,0.80)' }}
              >
                {eyebrow}
              </div>
            )}

            <div className="mt-1 flex items-center gap-2">
              <div
                className="grid h-9 w-9 place-items-center rounded-2xl border"
                style={{
                  borderColor: AURORA.border,
                  background: iconBg,
                }}
                aria-hidden
              >
                {Icon ? (
                  <Icon
                    className="h-4 w-4"
                    style={{ color: AURORA.text2 }}
                    strokeWidth={2}
                  />
                ) : null}
              </div>

              <h3
                className="text-[13px] font-semibold tracking-tight"
                style={{ color: AURORA.text }}
              >
                {title}
              </h3>
            </div>
          </div>

          {rightSlot ? <div className="shrink-0">{rightSlot}</div> : null}
        </div>

        <div
          className="mt-3 text-[13px] leading-relaxed"
          style={{ color: AURORA.text2 }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   “Leitura rápida”
============================================================ */
function LeituraRapidaInsight({ compact = false, data }) {
  const titulo = data?.titulo || 'Você sobe o score dominando os 4 pilares';
  const sugestao =
    data?.sugestao || 'Sugestão: foque no menor % da lista para ganhar tração.';

  return (
    <AuroraCard
      eyebrow="LEITURA RÁPIDA"
      title="Score"
      icon={Trophy}
      accent="amber"
      compact={compact}
    >
      <div className="flex flex-col gap-2">
        <div
          className="text-[13px] font-semibold"
          style={{ color: AURORA.text }}
        >
          {titulo}
        </div>
        <div className="text-[12px]" style={{ color: AURORA.text3 }}>
          {sugestao}
        </div>
      </div>
    </AuroraCard>
  );
}

/* ============================================================
   ✅ ATUALIZADO — Card “Ponto de Atenção” (MOVIMENTO DO MÊS)
   - remove setas / play / pause / dica
   - mostra 3 mensagens
   - alterna a cada 5s
============================================================ */
function MovimentoAtencaoCard({
  compact,
  movimentoMensal = [],
  year,
  idxMonth, // opcional (se você quiser controlar pelo pai)
}) {
  const y = Number.isFinite(Number(year))
    ? Number(year)
    : new Date().getFullYear();

  const monthIndex = Number.isFinite(Number(idxMonth))
    ? Number(idxMonth)
    : y === new Date().getFullYear()
      ? new Date().getMonth()
      : 0;

  const cur = useMemo(() => {
    const arr = Array.isArray(movimentoMensal) ? movimentoMensal : [];
    const row = arr[monthIndex] || {};
    return {
      mesIdx: monthIndex,
      mes: MESES_PT[monthIndex],
      negocios: Number(row.negocios || 0),
      visitas: Number(row.visitas || 0),
      vendas: Number(row.vendas || row.fechamentos || 0),
    };
  }, [movimentoMensal, monthIndex]);

  // 3 mensagens, alternando a cada 5s
  const slides = useMemo(() => {
    const n = Number(cur.negocios || 0);
    const v = Number(cur.visitas || 0);
    const f = Number(cur.vendas || 0);

    const plural = (x, s, p) => (Number(x) === 1 ? s : p);

    return [
      `Você cadastrou ${n} ${plural(n, 'negócio', 'negócios')}.`,
      `Você visitou ${v} ${plural(v, 'imóvel', 'imóveis')}.`,
      `Você fechou ${f} ${plural(f, 'negócio', 'negócios')}.`,
    ];
  }, [cur.negocios, cur.visitas, cur.vendas]);

  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    setMsgIdx(0); // ao trocar mês/ano, reinicia
  }, [monthIndex, y]);

  useEffect(() => {
    const t = setInterval(() => {
      setMsgIdx((i) => (i + 1) % slides.length);
    }, 5000);
    return () => clearInterval(t);
  }, [slides.length]);

  const rightSlot = (
    <div
      className="rounded-2xl border px-3 py-2 text-[11px] font-semibold"
      style={{
        borderColor: AURORA.border,
        background: 'rgba(255,255,255,0.04)',
        color: AURORA.text,
      }}
      title="Mês exibido"
    >
      {cur.mes}/{String(y).slice(-2)}
    </div>
  );

  return (
    <AuroraCard
      eyebrow="PONTO DE ATENÇÃO"
      title="Ritmo do mês"
      icon={AlertTriangle}
      accent="amber"
      compact={compact}
      tone="default"
      rightSlot={rightSlot}
    >
      {/* container no “tamanho do retângulo vermelho”:
          - bloco alto/encorpado
          - sem UI extra */}
      <div
        className="mt-1 rounded-2xl border p-4"
        style={{
          borderColor: AURORA.border,
          background: 'rgba(255,255,255,0.03)',
        }}
      >
        <div className="text-[12px]" style={{ color: AURORA.text3 }}>
          Movimento do mês
        </div>

        <div
          className="mt-2 text-[15px] font-extrabold tracking-tight"
          style={{ color: AURORA.text }}
        >
          {slides[msgIdx]}
        </div>

        {/* indicadores discretos */}
        <div className="mt-4 flex items-center gap-1.5">
          {slides.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === msgIdx ? 22 : 8,
                background:
                  i === msgIdx
                    ? 'rgba(52,211,153,0.85)'
                    : 'rgba(255,255,255,0.18)',
              }}
            />
          ))}
        </div>
      </div>
    </AuroraCard>
  );
}

export default function InsightPanel({
  loading,
  negociosPorStatus = {},
  vendasRecentes = [],
  visitasMes = 0,
  imoveisAtivos = 0,
  honorariosMeses = [],

  // ✅ movimento anual (12 meses)
  movimentoMensal = [],
  year,

  compact = false,
  dark = false, // mantido por compatibilidade
}) {
  // ✅ Hooks sempre antes de returns condicionais
  const totalNegocios = useMemo(() => {
    return Object.values(negociosPorStatus || {}).reduce(
      (acc, v) => acc + (Number(v) || 0),
      0
    );
  }, [negociosPorStatus]);

  const totalFechamentos = useMemo(
    () => (vendasRecentes || []).length,
    [vendasRecentes]
  );

  const { insight, attention, status, leituraRapida } = useMemo(() => {
    const res = leadershipEngine({
      totalNegocios,
      totalFechamentos,
      visitasMes: Number(visitasMes) || 0,
      imoveisAtivos: Number(imoveisAtivos) || 0,
      honorariosMeses: Array.isArray(honorariosMeses) ? honorariosMeses : [],
    });

    return {
      ...res,
      leituraRapida: res?.leituraRapida || null,
    };
  }, [
    totalNegocios,
    totalFechamentos,
    visitasMes,
    imoveisAtivos,
    honorariosMeses,
  ]);

  if (loading) {
    return (
      <div
        className={`h-[96px] animate-pulse rounded-3xl border bg-zinc-950/80 backdrop-blur ${
          compact ? 'p-4' : 'p-5'
        }`}
        style={{ borderColor: AURORA.border }}
      />
    );
  }

  const atencaoTipo = attention?.tipo || 'tensao';
  const tone =
    atencaoTipo === 'risco'
      ? 'danger'
      : atencaoTipo === 'ajuste'
        ? 'warning'
        : 'success';

  return (
    <section className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
      {/* ✅ Card 00 — Ponto de atenção (primeiro) */}
      <MovimentoAtencaoCard
        compact={compact}
        movimentoMensal={movimentoMensal}
        year={year}
      />

      {/* Card 01 — Leitura rápida */}
      <LeituraRapidaInsight compact={compact} data={leituraRapida} />

      {/* Card 02 — Insight */}
      <AuroraCard
        eyebrow="LEITURA INTELIGENTE"
        title="Insight"
        icon={Lightbulb}
        accent="emerald"
        compact={compact}
      >
        {insight}
      </AuroraCard>

      {/* Card 03 — Seu nível */}
      <AuroraCard
        eyebrow="POSICIONAMENTO"
        title="Seu nível"
        icon={Flame}
        accent="purple"
        compact={compact}
        tone={tone}
      >
        <div className="flex flex-col gap-1">
          <span
            className="text-base font-extrabold"
            style={{ color: AURORA.text }}
          >
            {status?.nivel || 'Corretor Iniciante'}
          </span>

          <span className="text-[12px]" style={{ color: AURORA.text2 }}>
            Média mensal: {formatMoney(status?.media || 0)}
          </span>
        </div>

        <p className="mt-2 text-[11px]" style={{ color: AURORA.text3 }}>
          {status?.mensagem ||
            'Você está construindo base. O foco agora é método e consistência.'}
        </p>
      </AuroraCard>
    </section>
  );
}
